import React, { useState, useEffect, useCallback } from 'react';
import { BsSearch, BsX } from 'react-icons/bs';
import { toast } from 'react-toastify';
import useTokenStore from '../../stores/tokens';
import { compatApi } from '../../utils/Intercepter';
import { PURPLE, MINT, hexToRgba } from '../../utils/designUtils';

export default function AdminSettings() {
    const { user } = useTokenStore();
    const [triggers, setTriggers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    // 수신자 선택 모달
    const [showModal, setShowModal] = useState(false);
    const [activeTrigger, setActiveTrigger] = useState(null); // { triggerCode, label }
    const [searchKeyword, setSearchKeyword] = useState('');
    const [allUserList, setAllUserList] = useState([]);
    const [selectedUser, setSelectedUser] = useState([]);
    const [loadingUsers, setLoadingUsers] = useState(false);

    const loadSettings = useCallback(async () => {
        try {
            const response = await compatApi.get('/admin/settings/triggers');
            if (response.ok && response.data.result) {
                setTriggers(response.data.data || []);
            } else {
                setTriggers([]);
            }
        } catch (error) {
            console.error('어드민 설정 조회 오류:', error);
            setTriggers([]);
        }
        setLoading(false);
    }, []);

    useEffect(() => {
        loadSettings();
    }, [loadSettings]);

    const loadUserList = useCallback(async () => {
        setLoadingUsers(true);
        try {
            const response = await compatApi.get('/admin/user/readUserList');
            if (response.ok) {
                const uniqueUsers = Array.from(
                    new Map(response.data.map(item => [item.userEmail, item])).values()
                );
                const userList = uniqueUsers || [];
                if (userList.length > 0) {
                    const emails = userList.map(p => p.userEmail);
                    try {
                        const deviceResponse = await compatApi.post('/push/device/find', emails);
                        if (deviceResponse.ok) {
                            const appUserEmails = deviceResponse.data || [];
                            setAllUserList(userList.map(person => ({
                                ...person,
                                isAppUser: appUserEmails.includes(person.userEmail)
                            })));
                            return;
                        }
                    } catch (deviceError) {
                        console.error('앱 사용자 조회 오류:', deviceError);
                    }
                }
                setAllUserList(userList);
            } else {
                toast.error('사용자 목록을 불러오는데 실패했습니다.');
            }
        } catch (error) {
            console.error('사용자 목록 조회 오류:', error);
            toast.error('사용자 목록을 불러오는 중 오류가 발생했습니다.');
        } finally {
            setLoadingUsers(false);
        }
    }, []);

    const getFilteredUserList = () => {
        const keyword = searchKeyword.trim().toLowerCase();
        if (!keyword) return allUserList;
        return allUserList.filter(person =>
            (person.userName && person.userName.toLowerCase().includes(keyword)) ||
            (person.userEmail && person.userEmail.toLowerCase().includes(keyword))
        );
    };

    const handleToggleEnabled = async (trigger) => {
        setSaving(true);
        const next = !trigger.enabled;
        try {
            const response = await compatApi.put(
                `/admin/settings/trigger/${trigger.triggerCode}/enabled`,
                { enabled: next }
            );
            if (response.ok && response.data.result) {
                setTriggers(prev => prev.map(t =>
                    t.triggerCode === trigger.triggerCode ? { ...t, enabled: next } : t
                ));
            } else {
                toast.error(response.data.message || '설정 변경에 실패했습니다.');
            }
        } catch (error) {
            console.error('트리거 설정 오류:', error);
            toast.error('설정 변경 중 오류가 발생했습니다.');
        } finally {
            setSaving(false);
        }
    };

    const handleToggleUser = (person) => {
        const isSelected = selectedUser.some(p => p.userEmail === person.userEmail);
        if (isSelected) setSelectedUser(selectedUser.filter(p => p.userEmail !== person.userEmail));
        else setSelectedUser([...selectedUser, person]);
    };

    const handleOpenModal = (trigger) => {
        setActiveTrigger(trigger);
        setSelectedUser((trigger.recipients || []).map(r => ({ userEmail: r.email, userName: r.userName })));
        setSearchKeyword('');
        setShowModal(true);
        loadUserList();
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setActiveTrigger(null);
        setSearchKeyword('');
        setSelectedUser([]);
    };

    const saveRecipients = async (triggerCode, emailList) => {
        const response = await compatApi.put(
            `/admin/settings/trigger/${triggerCode}/recipients`,
            { emailList }
        );
        if (!(response.ok && response.data.result)) {
            throw new Error(response.data?.message || '수신자 설정에 실패했습니다.');
        }
    };

    const handleSelectComplete = async () => {
        if (!activeTrigger) return;
        setSaving(true);
        try {
            await saveRecipients(activeTrigger.triggerCode, selectedUser.map(item => item.userEmail));
            toast.success('수신자가 설정되었습니다.');
            await loadSettings();
            handleCloseModal();
        } catch (error) {
            console.error('수신자 설정 오류:', error);
            toast.error(error.message || '수신자 설정 중 오류가 발생했습니다.');
        } finally {
            setSaving(false);
        }
    };

    const handleToggleRecipient = async (trigger, recipient) => {
        const next = !recipient.enabled;
        // 낙관적 업데이트
        setTriggers(prev => prev.map(t =>
            t.triggerCode === trigger.triggerCode
                ? { ...t, recipients: t.recipients.map(r => r.email === recipient.email ? { ...r, enabled: next } : r) }
                : t
        ));
        try {
            const response = await compatApi.put(
                `/admin/settings/trigger/${trigger.triggerCode}/recipient/enabled`,
                { email: recipient.email, enabled: next }
            );
            if (!(response.ok && response.data.result)) {
                toast.error(response.data?.message || '설정 변경에 실패했습니다.');
                await loadSettings(); // 롤백
            }
        } catch (error) {
            console.error('수신자 on/off 오류:', error);
            toast.error('설정 변경 중 오류가 발생했습니다.');
            await loadSettings(); // 롤백
        }
    };

    const handleDeleteRecipient = async (trigger, email) => {
        if (!window.confirm('해당 수신자를 삭제하시겠습니까?')) return;
        setSaving(true);
        try {
            const newList = (trigger.recipients || [])
                .filter(r => r.email !== email)
                .map(r => r.email);
            await saveRecipients(trigger.triggerCode, newList);
            toast.success('삭제되었습니다.');
            await loadSettings();
        } catch (e) {
            console.error('삭제 오류:', e);
            toast.error(e.message || '삭제 실패');
        } finally {
            setSaving(false);
        }
    };

    const handleTemplateField = (triggerCode, field, value) => {
        setTriggers(prev => prev.map(t =>
            t.triggerCode === triggerCode ? { ...t, [field]: value } : t
        ));
    };

    const handleSaveTemplate = async (trigger) => {
        setSaving(true);
        try {
            const response = await compatApi.put(
                `/admin/settings/trigger/${trigger.triggerCode}/template`,
                { title: trigger.title, body: trigger.body }
            );
            if (response.ok && response.data.result) {
                toast.success('발송 문구가 저장되었습니다.');
            } else {
                toast.error(response.data?.message || '발송 문구 저장에 실패했습니다.');
            }
        } catch (error) {
            console.error('발송 문구 저장 오류:', error);
            toast.error('발송 문구 저장 중 오류가 발생했습니다.');
        } finally {
            setSaving(false);
        }
    };

    // --- Design System Helpers ---
    const cardS = {
        background: 'white', borderRadius: 16,
        border: '1px solid #f0eff5',
        boxShadow: '0 1px 2px rgba(60,50,120,.04), 0 4px 16px -8px rgba(60,50,120,.08)',
        overflow: 'hidden',
    };

    const inp = {
        height: 36, borderRadius: 8, border: '1.5px solid #ececf2', background: '#fafaff',
        padding: '0 12px', fontSize: 13.5, color: '#1f1d2e', outline: 'none',
        fontFamily: 'inherit', width: '100%', boxSizing: 'border-box'
    };

    const b = (variant, isSmall) => {
        const h = isSmall ? 28 : 38;
        const p = isSmall ? '0 12px' : '0 20px';
        const base = { height: h, padding: p, borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: isSmall ? 12 : 14, fontWeight: 600, fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, whiteSpace: 'nowrap', transition: 'all 0.2s' };
        if (variant === 'primary') return { ...base, background: PURPLE, color: 'white' };
        if (variant === 'outline-secondary') return { ...base, background: 'white', color: '#6b7280', border: '1px solid #d1d5db' };
        if (variant === 'outline-primary') return { ...base, background: 'white', color: PURPLE, border: `1px solid ${hexToRgba(PURPLE, .4)}` };
        return { ...base, background: '#e5e7eb', color: '#374151' };
    };

    const Toggle = ({ on, onClick, disabled }) => (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            aria-pressed={on}
            style={{
                width: 46, height: 26, borderRadius: 999, border: 'none', position: 'relative',
                cursor: disabled ? 'not-allowed' : 'pointer', padding: 0,
                background: on ? PURPLE : '#d4d3df', transition: 'background 0.2s', flexShrink: 0,
                opacity: disabled ? 0.6 : 1,
            }}
        >
            <span style={{
                position: 'absolute', top: 3, left: on ? 23 : 3, width: 20, height: 20, borderRadius: '50%',
                background: 'white', transition: 'left 0.2s', boxShadow: '0 1px 2px rgba(0,0,0,.2)'
            }} />
        </button>
    );

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 'calc(100vh - 61px)', background: '#f5f4fa' }}>
                <div style={{ width: 32, height: 32, borderRadius: '50%', border: `3px solid ${hexToRgba(PURPLE, 0.1)}`, borderTopColor: PURPLE, animation: 'spin 0.8s linear infinite' }} />
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
        );
    }

    return (
        <div style={{ background: '#f5f4fa', minHeight: 'calc(100vh - 61px)', padding: '32px clamp(20px, 3vw, 40px)' }}>
            <div style={{ maxWidth: 1600, margin: '0 auto', padding: '32px clamp(20px, 3vw, 40px) 48px', boxSizing: 'border-box' }}>
                <div style={{ marginBottom: 24 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6, fontSize: 12, color: '#8b8a99' }}>
                        <span>관리자</span>
                        <span style={{ fontSize: 10 }}>›</span>
                        <span style={{ color: '#3a3849', fontWeight: 500 }}>어드민 설정</span>
                    </div>
                    <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: '#1f1d2e', letterSpacing: '-0.01em' }}>어드민 설정</h1>
                    <p style={{ margin: '8px 0 0', fontSize: 13.5, color: '#5d5b6e', lineHeight: 1.6 }}>
                        트리거별로 알림 푸시 발송 여부와 받을 사람을 설정합니다. 켜진 트리거는 지정된 수신자 전원에게 발송됩니다.
                    </p>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {triggers.map((trigger) => (
                        <div key={trigger.triggerCode} style={cardS}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', background: 'linear-gradient(180deg,#faf9fd 0%,#f5f4fa 100%)', borderBottom: '1px solid #ececf2' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                    <span style={{ width: 4, height: 16, borderRadius: 2, background: `linear-gradient(180deg,${PURPLE},${MINT})`, flexShrink: 0, display: 'inline-block' }} />
                                    <span style={{ fontSize: 14.5, fontWeight: 600, color: '#1f1d2e' }}>{trigger.label}</span>
                                    <span style={{ fontSize: 11, fontWeight: 600, color: trigger.enabled ? MINT : '#aaa9b7', background: trigger.enabled ? hexToRgba(MINT, 0.12) : '#f0eff5', padding: '2px 8px', borderRadius: 6 }}>
                                        {trigger.enabled ? 'ON' : 'OFF'}
                                    </span>
                                </div>
                                <Toggle on={trigger.enabled} disabled={saving} onClick={() => handleToggleEnabled(trigger)} />
                            </div>

                            <div style={{ padding: '20px', opacity: trigger.enabled ? 1 : 0.55, transition: 'opacity 0.2s' }}>
                                <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr', alignItems: 'start' }}>
                                    <label style={{ fontWeight: 600, fontSize: 13.5, color: '#5d5b6e', paddingTop: 10 }}>수신자</label>
                                    <div>
                                        {trigger.recipients && trigger.recipients.length > 0 ? (
                                            <div style={{ border: '1.5px solid #ececf2', borderRadius: 10, overflow: 'hidden', marginBottom: 10, background: '#fafaff' }}>
                                                {trigger.recipients.map((r, idx) => (
                                                    <div
                                                        key={r.email}
                                                        style={{
                                                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                                            padding: '12px 16px', borderBottom: idx < trigger.recipients.length - 1 ? '1.5px solid #f4f3f9' : 'none',
                                                            background: 'white'
                                                        }}
                                                    >
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, opacity: r.enabled ? 1 : 0.45, transition: 'opacity 0.15s' }}>
                                                            <div style={{ width: 34, height: 34, borderRadius: 10, background: hexToRgba(PURPLE, .1), color: PURPLE, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 14 }}>
                                                                {(r.userName || r.email)?.charAt(0)}
                                                            </div>
                                                            <div>
                                                                <div style={{ fontWeight: 600, fontSize: 14, color: '#1f1d2e' }}>{r.userName || '(이름 없음)'}</div>
                                                                <div style={{ fontSize: 12, color: '#8b8a99', marginTop: 1 }}>{r.email}</div>
                                                            </div>
                                                        </div>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                                                            <Toggle on={r.enabled} disabled={saving} onClick={() => handleToggleRecipient(trigger, r)} />
                                                            <button
                                                                onClick={() => handleDeleteRecipient(trigger, r.email)}
                                                                disabled={saving}
                                                                style={{ background: 'none', border: 'none', cursor: saving ? 'not-allowed' : 'pointer', padding: 4 }}
                                                                title="삭제"
                                                            >
                                                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-2 14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L5 6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>
                                                            </button>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <div style={{ padding: '24px 16px', border: '1.5px dashed #ececf2', borderRadius: 10, background: '#fafaff', color: '#8b8a99', textAlign: 'center', marginBottom: 10, fontSize: 13.5 }}>
                                                지정된 수신자가 없습니다.
                                            </div>
                                        )}
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <p style={{ fontSize: 11.5, color: '#8b8a99', margin: 0 }}>검색을 통해 구성원을 선택할 수 있습니다.</p>
                                            <button
                                                onClick={() => handleOpenModal(trigger)}
                                                disabled={saving}
                                                style={b('outline-primary', true)}
                                            >
                                                <BsSearch size={12} /> 구성원 검색
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <div style={{ marginTop: 20, paddingTop: 20, borderTop: '1px solid #f4f3f9' }}>
                                    <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr', alignItems: 'start' }}>
                                        <label style={{ fontWeight: 600, fontSize: 13.5, color: '#5d5b6e', paddingTop: 10 }}>발송 문구</label>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                            <div>
                                                <span style={{ fontSize: 12, color: '#8b8a99', display: 'block', marginBottom: 4 }}>제목</span>
                                                <input
                                                    value={trigger.title || ''}
                                                    onChange={(e) => handleTemplateField(trigger.triggerCode, 'title', e.target.value)}
                                                    style={inp}
                                                    placeholder="푸시 제목"
                                                />
                                            </div>
                                            <div>
                                                <span style={{ fontSize: 12, color: '#8b8a99', display: 'block', marginBottom: 4 }}>본문</span>
                                                <textarea
                                                    value={trigger.body || ''}
                                                    onChange={(e) => handleTemplateField(trigger.triggerCode, 'body', e.target.value)}
                                                    rows={3}
                                                    style={{ ...inp, height: 'auto', padding: '10px 12px', lineHeight: 1.5, resize: 'vertical' }}
                                                    placeholder="푸시 본문 (HTML 가능)"
                                                />
                                            </div>
                                            {trigger.placeholders && (
                                                <div style={{ fontSize: 11.5, color: '#8b8a99', background: '#fafaff', border: '1px solid #f0eff5', borderRadius: 8, padding: '8px 10px' }}>
                                                    치환자 <strong style={{ color: PURPLE }}>{trigger.placeholders}</strong> — 발송 시 실제 값으로 치환됩니다.
                                                </div>
                                            )}
                                            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                                <button onClick={() => handleSaveTemplate(trigger)} disabled={saving} style={b('primary', true)}>
                                                    {saving ? '저장 중...' : '문구 저장'}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {showModal && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1050, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
                    <div style={{ background: 'white', borderRadius: 16, width: '100%', maxWidth: 540, boxShadow: '0 8px 32px rgba(60,50,120,.2)', display: 'flex', flexDirection: 'column', maxHeight: '85vh', overflow: 'hidden' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px', borderBottom: '1px solid #ececf2' }}>
                            <h5 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#1f1d2e' }}>
                                수신자 선택 {activeTrigger ? `· ${activeTrigger.label}` : ''}
                            </h5>
                            <button onClick={handleCloseModal} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#8b8a99', padding: 4 }}>
                                <BsX size={24} />
                            </button>
                        </div>

                        <div style={{ padding: '16px 24px', borderBottom: '1px solid #f4f3f9', background: '#fafaff' }}>
                            <div style={{ display: 'flex', alignItems: 'center', border: '1.5px solid #ececf2', borderRadius: 10, background: 'white', overflow: 'hidden', padding: '0 12px' }}>
                                <BsSearch size={14} color="#8b8a99" />
                                <input
                                    type="text"
                                    placeholder="이름이나 이메일로 검색"
                                    value={searchKeyword}
                                    onChange={(e) => setSearchKeyword(e.target.value)}
                                    style={{ flex: 1, height: 40, border: 'none', background: 'transparent', fontSize: 14, color: '#1f1d2e', outline: 'none', fontFamily: 'inherit', paddingLeft: 10 }}
                                />
                            </div>
                        </div>

                        <div style={{ flex: 1, overflowY: 'auto', minHeight: 300 }}>
                            {loadingUsers ? (
                                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                                    <div style={{ width: 28, height: 28, borderRadius: '50%', border: `3px solid ${hexToRgba(PURPLE, 0.1)}`, borderTopColor: PURPLE, animation: 'spin 0.8s linear infinite' }} />
                                </div>
                            ) : getFilteredUserList().length > 0 ? (
                                getFilteredUserList().map((person) => {
                                    const isSelected = selectedUser.some(p => p.userEmail === person.userEmail);
                                    return (
                                        <div
                                            key={person.userEmail}
                                            onClick={() => handleToggleUser(person)}
                                            style={{
                                                display: 'flex', alignItems: 'center', padding: '12px 24px',
                                                cursor: 'pointer', borderBottom: '1px solid #f4f3f9',
                                                background: isSelected ? hexToRgba(PURPLE, 0.04) : 'white',
                                                transition: 'background 0.1s'
                                            }}
                                            onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = '#fafaff'; }}
                                            onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = 'white'; }}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={isSelected}
                                                readOnly
                                                style={{ accentColor: PURPLE, width: 16, height: 16, marginRight: 16, cursor: 'pointer' }}
                                            />
                                            <div style={{ flex: 1 }}>
                                                <div style={{ fontSize: 14, fontWeight: 600, color: '#1f1d2e' }}>{person.userName}</div>
                                                <div style={{ fontSize: 12.5, color: '#8b8a99', marginTop: 2 }}>{person.userEmail}</div>
                                            </div>
                                            {person.isAppUser && (
                                                <span style={{ fontSize: 10.5, fontWeight: 700, color: MINT, background: hexToRgba(MINT, 0.12), padding: '2px 8px', borderRadius: 6 }}>App</span>
                                            )}
                                        </div>
                                    );
                                })
                            ) : (
                                <div style={{ textAlign: 'center', color: '#aaa9b7', padding: '60px 0', fontSize: 14 }}>
                                    <div style={{ marginBottom: 12 }}><BsSearch size={32} opacity={0.3} /></div>
                                    {searchKeyword ? '검색 결과가 없습니다.' : '사용자 목록이 없습니다.'}
                                </div>
                            )}
                        </div>

                        <div style={{ padding: '16px 24px', borderTop: '1px solid #ececf2', background: '#fafaff', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
                            <span style={{ fontSize: 12.5, color: '#8b8a99' }}>{selectedUser.length}명 선택됨</span>
                            <div style={{ display: 'flex', gap: 10 }}>
                                <button style={b('outline-secondary')} onClick={handleCloseModal}>취소</button>
                                <button
                                    onClick={handleSelectComplete}
                                    disabled={saving}
                                    style={b('primary')}
                                >
                                    {saving ? '저장 중...' : '선택 완료'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
    );
}
